import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IArchivoAdjunto, IArchivoAdjuntoFirma, IPosXY, ISignFile, ISolicitudFirma } from '../interface/sign.interface';
import { lastValueFrom } from 'rxjs';
import { DataSignService } from './data.service';
import { UserInteractiveService } from '@/Services/user-interactive.service';
import { ConfirmationService } from 'primeng/api';
import { FilePreviewComponent } from '@/shared/components/general/file-preview/file-preview.component';
import { IFirma } from '../../botonera.interface';

@Injectable({
  providedIn: 'root',
})
export class SignGenericService {
  // Servicios
  dialogConfig = inject(DynamicDialogConfig);
  dialogRef = inject(DynamicDialogRef);
  dataService = inject(DataSignService);
  userInteractive = inject(UserInteractiveService);
  confirmationService = inject(ConfirmationService);

  // Parametros de interfaz
  getPosXY: Signal<IPosXY[]> = computed(() => this.dialogConfig.data?.initialData?.Pos_x_y ?? []);
  getImagenesFirma: Signal<ISignFile[]> = computed(() => this.dialogConfig.data?.initialData?.FirmasArchivo ?? []);
  getRegistro: Signal<IFirma> = computed(() => this.dialogConfig.data?.registro ?? []);
  isDocumentSign = signal<boolean>(this.dialogConfig.data?.registro?.SelloVirtualInsertado ?? false);

  selPosXy = signal<IPosXY | null>(null);
  selImagenFirma = signal<ISignFile | null>(null);
  selSignFile = signal<IArchivoAdjuntoFirma | null>(null);
  
  // metodos
  async getSignFile(url: string): Promise<IArchivoAdjuntoFirma> {
    const fileSign = await lastValueFrom(this.dataService.getFile(url));
    this.selSignFile.set(fileSign);
    return fileSign;
  }
  
  async onPreview(): Promise<IArchivoAdjuntoFirma | undefined> {
    if  (this.selImagenFirma() && this.selPosXy()) {
      const fileSigned = (await lastValueFrom(this.dataService.postFirmarVistaPrevia(this.getSolicitudFirmaPrevisualizar())));
      this.onOpenDocument(fileSigned);
    }
    return undefined;
  }

  async onSign(): Promise<IArchivoAdjuntoFirma | undefined> {
    if (this.selImagenFirma() && this.selPosXy()) {
      this.confirmationService.confirm({
        message: '¿Desea firmar el documento?',
        header: 'Confirmación',
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.processFileSign(),
      });
    }
    return undefined;
  }

  // File
  onOpenDocument(file: IArchivoAdjunto) {
    const config: DynamicDialogConfig = {
        header: 'Visualizar documento',
        data: { fileType: file.FileType, fileUrl: file.FileBase64,
      },
    }        
    return this.userInteractive.openDialog(FilePreviewComponent, config);
  }

  async processFileSign() {    
    const fileSigned = (await lastValueFrom(this.dataService.postFirmar(this.getSolicitudFirmaPrevisualizar())));

    if (fileSigned) {
      const refDoc = this.onOpenDocument(fileSigned);
      this.isDocumentSign.set(true);
      await lastValueFrom(refDoc.onClose);
      this.dialogRef.close(true);
    }
  }

  getSolicitudFirmaPrevisualizar() {
    const registro: IFirma = this.dialogConfig.data?.registro;
    
    const request: ISolicitudFirma = {
      IdfdArchivoFirma: this.selImagenFirma()!.IdArchivosAdjuntos,
      IdPosxy: this.selPosXy()!.idPosxy,
      TipoFirma: registro.tipoFirma,
      FechaFirma: new Date(),
      IdfdArchivo: registro.IdfdArchivo,
      TipoFirmaClasificacion: registro.clasificacion,
      ArchivoFirma: this.selSignFile()?.NombreArchivo,
      ModoRocket: 0,
      InsertarFecha: true,
      Masivo: registro.Masivo ?? false,
      CodfdCuerpoMasivo:registro.CodfdCuerpoMasivo??0
    }
    return request;
  }
}
